import React, { useMemo, useState } from 'react';
import { RefreshCw, Mail, MailCheck, MailX, ExternalLink, Search } from 'lucide-react';
import type { TenantRow } from '../../types/tenantRegistration';

interface TenantListProps {
  rows: TenantRow[];
  loading: boolean;
  hasApiSession: boolean;
  apiError: string;
  onRefresh: () => void;
  onOpenEmail: (row: TenantRow) => void;
  onResend: (row: TenantRow) => void;
  resendingTenant: string | null;
}

function formatDate(value: string | null): string
{
  if (!value) return '—';
  const date = new Date(value);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

const EmailStatus: React.FC<{ row: TenantRow }> = ({ row }) =>
{
  const last = row.lastEmail;
  if (!last)
  {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-gray-400">
        <Mail className="w-3.5 h-3.5" /> No record
      </span>
    );
  }
  const label = last.kind === 'welcome' ? 'Welcome email' : 'Access email';
  if (last.ok)
  {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-green-700"
        title={`${label} sent ${formatDate(last.sentAt)}`}
      >
        <MailCheck className="w-3.5 h-3.5 shrink-0" /> {label} sent
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 text-xs text-red-600"
      title={last.error || 'Failed'}
    >
      <MailX className="w-3.5 h-3.5" /> {label} failed
    </span>
  );
};

export const TenantList: React.FC<TenantListProps> = ({
  rows,
  loading,
  hasApiSession,
  apiError,
  onRefresh,
  onOpenEmail,
  onResend,
  resendingTenant,
}) =>
{
  const [filter, setFilter] = useState('');

  const filtered = useMemo(() =>
  {
    const term = filter.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(
      row =>
        row.tenancyName.toLowerCase().includes(term) ||
        row.emailAddress.toLowerCase().includes(term) ||
        row.name.toLowerCase().includes(term),
    );
  }, [rows, filter]);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b border-gray-200">
        <div>
          <h2 className="text-lg font-medium text-gray-800">Tenants</h2>
          <p className="text-xs text-gray-500">
            {hasApiSession
              ? 'Server list merged with the emails sent from this console.'
              : 'Server list unavailable (registrar has no API session). Showing tenants created from this browser.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={filter}
              onChange={e => setFilter(e.target.value)}
              placeholder="Search tenant or email"
              className="pl-8 pr-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#62accb] focus:border-transparent"
            />
          </div>
          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {apiError && (
        <div className="mx-4 mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {apiError}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Tenant</th>
              <th className="text-left px-4 py-2 font-medium whitespace-nowrap">Administrator email</th>
              <th className="text-left px-4 py-2 font-medium">Status</th>
              <th className="text-left px-4 py-2 font-medium">Created</th>
              <th className="text-left px-4 py-2 font-medium">Users</th>
              <th className="text-left px-4 py-2 font-medium">Email</th>
              <th className="text-right px-4 py-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                  {loading ? 'Loading tenants...' : 'No tenants to show.'}
                </td>
              </tr>
            )}
            {filtered.map(row =>
            {
              const resending = resendingTenant === row.tenancyName;
              return (
                <tr key={row.tenancyName} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 max-w-[220px]">
                    <div className="font-medium text-gray-800">{row.tenancyName}</div>
                    <a
                      href={row.tenantUrl}
                      target="_blank"
                      rel="noreferrer"
                      title={row.tenantUrl}
                      className="flex items-center gap-1 text-xs text-[#62accb] hover:underline"
                    >
                      <span className="truncate">{row.tenantUrl}</span>
                      <ExternalLink className="w-3 h-3 shrink-0" />
                    </a>
                  </td>
                  <td className="px-4 py-2.5">
                    {row.emailAddress ? (
                      <button
                        type="button"
                        onClick={() => onOpenEmail(row)}
                        className="text-[#62accb] hover:underline text-left break-all"
                        title="Open tenant access and server configuration"
                      >
                        {row.emailAddress}
                      </button>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    {row.isActive === null ? (
                      <span className="text-xs text-gray-400">Unknown</span>
                    ) : row.isActive ? (
                      <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">
                        Active
                      </span>
                    ) : (
                      <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-gray-200 text-gray-600">
                        Inactive
                      </span>
                    )}
                    {row.source === 'local' && (
                      <span
                        className="ml-1 inline-block px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-600"
                        title="Created from this browser, not confirmed by the server list"
                      >
                        Local
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap">
                    {formatDate(row.createdAt)}
                  </td>
                  <td className="px-4 py-2.5 text-gray-600">
                    {row.registeredUsers === null ? '—' : row.registeredUsers}
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    <EmailStatus row={row} />
                    {row.lastEmail && (
                      <div className="text-xs text-gray-400">
                        to {row.lastEmail.sentTo} · {formatDate(row.lastEmail.sentAt)}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => onResend(row)}
                      disabled={resending || !row.emailAddress}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs bg-[#62accb] hover:bg-[#4a9bd1] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Mail className="w-3.5 h-3.5" />
                      {resending ? 'Sending...' : 'Resend email'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
